import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

/** Routes that load large amounts of data. Clearing their cache on leave frees memory. */
const HEAVY_ROUTES = [
  '/3danalysis_step1',
  '/3danalysis_step2',
  '/3danalysis_step3',
  '/3danalysis_step4',
  '/historical-declines',
  '/business-metrics',
  '/causality-report',
  '/360/owner-funnel',
  '/360/buyer-funnel',
  '/visual-generation',
  '/all-contacts',
];

/** Query keys to clear when leaving heavy pages. */
const HEAVY_QUERY_KEYS = [
  ['business-metrics'],
  ['business-metrics-chart-data'],
];

/**
 * Clears React Query cache for heavy pages when navigating away.
 * Call this hook from a component inside the router (e.g. App).
 */
export function useClearHeavyPageCache() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;

    const wasOnHeavyRoute = HEAVY_ROUTES.some((route) => prevPath.startsWith(route));
    const leftHeavyRoute = wasOnHeavyRoute && !HEAVY_ROUTES.some((route) => currentPath.startsWith(route));

    if (leftHeavyRoute) {
      HEAVY_QUERY_KEYS.forEach((queryKey) => {
        queryClient.removeQueries({ queryKey });
      });
    }

    prevPathRef.current = currentPath;
  }, [location.pathname, queryClient]);
}
